import styles from './_Settings.module.scss';
import PropTypes from 'prop-types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSettings, toggleOverallAppOverlay } from 'slices/AppSlice';
import {
    selectSettings,
    selectShowOverallAppOverlay
} from 'selectors/selectors';
import BlueCrossIcon from './images/blue-cross.svg';
import ThinBlueCrossIcon from './images/thin-blue-cross.svg';
import { SETTINGS_URL, THRESHOLD_OVERRIDES_URL } from 'constants/api';
import { headers } from 'api/constants';
import axios from 'axios';
import { isEmpty } from 'lodash';

const SCENARIO_ONE_THRESHOLD = 's1Threshold';
const SCENARIO_TWO_PORTFOLIO_THRESHOLD = 's2PortfolioThreshold';
const SCENARIO_TWO_CONCENTRATION_THRESHOLD = 's2ConcentrationThreshold';
const SCENARIO_THREE_THRESHOLD = 's3Threshold';
const SCENARIO_FOUR_THRESHOLD = 's4Threshold';

const BETWEEN_0_AND_100_ERROR_TEXT =
    'Override number must be between 0 and 100 (inclusive)';

const SCENARIOS = [
    {
        title: 'Scenario 1',
        scKeys: [SCENARIO_ONE_THRESHOLD]
    },
    {
        title: 'Scenario 2',
        scKeys: [
            SCENARIO_TWO_PORTFOLIO_THRESHOLD,
            SCENARIO_TWO_CONCENTRATION_THRESHOLD
        ]
    },
    {
        title: 'Scenario 3',
        scKeys: [SCENARIO_THREE_THRESHOLD]
    },
    {
        title: 'Scenario 4',
        scKeys: [SCENARIO_FOUR_THRESHOLD]
    }
];
const ScenarioValuesContainer = (props) => {
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(
                inputRef.current.value.length,
                inputRef.current.value.length
            );
        }
    }, []);
    const [elementInFocus, setElementInFocus] = useState(null);

    const {
        data,
        settings,
        inputValue,
        onChange,
        setters,
        error,
        idsWithError
    } = props;
    const { title, scKeys } = data;
    const isTitleScenarioTwo = title === 'Scenario 2';
    const handleFocus = (id) => {
        setElementInFocus(id);
    };

    const calculateInputClassName = (
        id,
        isScenarioTwo = false,
        isElementInFocus,
        value
    ) => {
        if (error && idsWithError.includes(id)) return styles.errorInput;
        if (isElementInFocus) {
            if (value) {
                return isScenarioTwo
                    ? styles.focusedAndHasValueScenarioTwoOverrideValueInput
                    : styles.focusedAndHasValueOverrideValueInput;
            } else {
                return isScenarioTwo
                    ? styles.focusedScenarioTwoOverrideValueInput
                    : styles.focusedOverrideValueInput;
            }
        } else {
            return isScenarioTwo
                ? styles.scenarioTwoOverrideValueInput
                : styles.overrideValueInput;
        }
    };

    return (
        <>
            <div className={isTitleScenarioTwo ? styles.s2Title : styles.title}>
                {title}
            </div>
            {isTitleScenarioTwo ? (
                <div
                    className={styles.scenarioTwoSystemCalculatedValueContainer}
                >
                    <div className={styles.scenarioTwoScSubContainer}>
                        <p>PRM</p>
                        {settings && (
                            <div
                                className={
                                    inputValue[0]
                                        ? styles.overriddenScenarioTwoSystemCalculatedValue
                                        : styles.scenarioTwoSystemCalculatedValue
                                }
                            >
                                {settings[scKeys[0]]}
                                <span className={styles.percentage}>%</span>
                            </div>
                        )}
                    </div>
                    <div className={styles.scenarioTwoScSubContainer}>
                        <p>CR</p>
                        {settings && (
                            <div
                                className={
                                    inputValue[1]
                                        ? styles.overriddenScenarioTwoSystemCalculatedValue
                                        : styles.scenarioTwoSystemCalculatedValue
                                }
                            >
                                {settings[scKeys[1]]}
                                <span className={styles.percentage}>%</span>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                settings && (
                    <div
                        className={
                            inputValue
                                ? styles.overriddenSystemCalculatedValue
                                : styles.systemCalculatedValue
                        }
                    >
                        {settings[scKeys[0]]}
                        <span className={styles.percentage}>%</span>
                    </div>
                )
            )}

            {isTitleScenarioTwo ? (
                <div className={styles.scenarioTwoInputsContainer}>
                    <input
                        id={scKeys[0]}
                        className={calculateInputClassName(
                            scKeys[0],
                            true,
                            elementInFocus === scKeys[0],
                            inputValue[0]
                        )}
                        value={inputValue[0] || ''}
                        onChange={(e) => onChange(e, scKeys[0])}
                        ref={inputRef}
                        onFocus={() => handleFocus(scKeys[0])}
                        onBlur={() => setElementInFocus(null)}
                    />
                    {elementInFocus === scKeys[0] && inputValue[0] && (
                        <img
                            id={`${scKeys[0]}-icon`}
                            src={ThinBlueCrossIcon}
                            alt={''}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setters.setS2PfThresholdValue('');
                            }}
                            className={styles.scenarioTwoPfCrossIcon}
                        />
                    )}
                    <input
                        id={scKeys[1]}
                        className={calculateInputClassName(
                            scKeys[1],
                            true,
                            elementInFocus === scKeys[1],
                            inputValue[1]
                        )}
                        value={inputValue[1] || ''}
                        onChange={(e) => onChange(e, scKeys[1])}
                        ref={inputRef}
                        onFocus={() => handleFocus(scKeys[1])}
                        onBlur={() => setElementInFocus(null)}
                    />
                    {elementInFocus === scKeys[1] && inputValue[1] && (
                        <img
                            id={`${scKeys[1]}-icon`}
                            src={ThinBlueCrossIcon}
                            alt={''}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setters.setS2ConcThresholdValue('');
                            }}
                            className={styles.scenarioTwoConcCrossIcon}
                        />
                    )}
                </div>
            ) : (
                <div className={styles.inputsContainer}>
                    <input
                        id={scKeys[0]}
                        className={calculateInputClassName(
                            scKeys[0],
                            false,
                            elementInFocus === scKeys[0],
                            inputValue
                        )}
                        value={inputValue || ''}
                        onChange={(e) => onChange(e, scKeys[0])}
                        ref={inputRef}
                        onFocus={() => handleFocus(scKeys[0])}
                        onBlur={() => setElementInFocus(null)}
                    />
                    {elementInFocus === scKeys[0] && inputValue && (
                        <img
                            id={`${scKeys[0]}-icon`}
                            src={ThinBlueCrossIcon}
                            alt={''}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                if (scKeys[0] === SCENARIO_ONE_THRESHOLD)
                                    setters.setS1ThresholdValue('');
                                if (scKeys[0] === SCENARIO_THREE_THRESHOLD)
                                    setters.setS3ThresholdValue('');
                                if (scKeys[0] === SCENARIO_FOUR_THRESHOLD)
                                    setters.setS4ThresholdValue('');
                            }}
                            className={styles.crossIcon}
                        />
                    )}
                </div>
            )}
        </>
    );
};

const Settings = () => {
    const dispatch = useDispatch();

    const [s1ThresholdValue, setS1ThresholdValue] = useState('');
    const [s2PfThresholdValue, setS2PfThresholdValue] = useState('');
    const [s2ConcThresholdValue, setS2ConcThresholdValue] = useState('');
    const [s3ThresholdValue, setS3ThresholdValue] = useState('');
    const [s4ThresholdValue, setS4ThresholdValue] = useState('');

    const [errorMsg, setErrorMsg] = useState('');
    const [idsWithError, setIdsWithError] = useState([]);

    const settings = useSelector(selectSettings);
    const showOverallAppOverlay = useSelector(selectShowOverallAppOverlay);

    useLayoutEffect(() => {
        dispatch(fetchSettings());
    }, []);

    useEffect(() => {
        fetchOverrides();
    }, [showOverallAppOverlay]);

    const inputValueArray = [
        s1ThresholdValue,
        [s2PfThresholdValue, s2ConcThresholdValue],
        s3ThresholdValue,
        s4ThresholdValue
    ];

    const doesAnyOfTheOverrideInputsHaveValue =
        s1ThresholdValue ||
        s2PfThresholdValue ||
        s2ConcThresholdValue ||
        s3ThresholdValue ||
        s4ThresholdValue;

    const getValueAndSetState = (thresholdType, data, setter) => {
        const value = data.find((d) => d.thresholdType === thresholdType);
        if (value) {
            validateInput(
                thresholdType,
                parseFloat(value.thresholdValue || '')
            );
            setter(value.thresholdValue || '');
        } else {
            setter('');
        }
    };

    const setStateValuesFromData = (overrides) => {
        getValueAndSetState(
            SCENARIO_ONE_THRESHOLD,
            overrides,
            setS1ThresholdValue
        );
        getValueAndSetState(
            SCENARIO_TWO_PORTFOLIO_THRESHOLD,
            overrides,
            setS2PfThresholdValue
        );
        getValueAndSetState(
            SCENARIO_TWO_CONCENTRATION_THRESHOLD,
            overrides,
            setS2ConcThresholdValue
        );
        getValueAndSetState(
            SCENARIO_THREE_THRESHOLD,
            overrides,
            setS3ThresholdValue
        );
        getValueAndSetState(
            SCENARIO_FOUR_THRESHOLD,
            overrides,
            setS4ThresholdValue
        );
    };

    const fetchOverrides = async () => {
        const response = await axios
            .get(`${SETTINGS_URL}`, { headers })
            .then((response) => response.data);
        const { overrides } = response;
        if (overrides.length === 0) return;
        setStateValuesFromData(overrides);
    };

    const addOverrides = async (params) => {
        const response = await axios
            .post(`${THRESHOLD_OVERRIDES_URL}`, params, { headers })
            .then((response) => response.data);
        const { overrides } = response;
        if (overrides.length === 0) return;
        setStateValuesFromData(overrides);
    };
    const handleCrossIconOnClick = () => {
        setIdsWithError([]);
        dispatch(toggleOverallAppOverlay());
    };

    const getPreviousValue = (key) => {
        if (key === SCENARIO_ONE_THRESHOLD) return s1ThresholdValue;
        if (key === SCENARIO_TWO_PORTFOLIO_THRESHOLD) return s2PfThresholdValue;
        if (key === SCENARIO_TWO_CONCENTRATION_THRESHOLD)
            return s2ConcThresholdValue;
        if (key === SCENARIO_THREE_THRESHOLD) return s3ThresholdValue;
        if (key === SCENARIO_FOUR_THRESHOLD) return s4ThresholdValue;
    };
    const verifyAndTransformValueIfNecessary = (value, previousValue) => {
        const oneDPRegex = new RegExp('^(\\d)*(\\.)?([0-9]{1})?$', 'gm');

        // check if value is empty and if it is return the value
        if (isEmpty(value)) return value;

        // check if value contains only numbers and/or decimal point and if not, then return previous value
        if (isNaN(parseFloat(value))) return previousValue;

        // test value against regex for any other special symbols or spaces or tabs
        if (!oneDPRegex.test(value)) return previousValue;

        // check if first character of value is 0 and if so, only a '.' can follow a 0
        if (value.charAt(0) === '0') {
            if (value.length === 1) return value;
            else return value.charAt(1) === '.' ? value : previousValue;
        }
        return value;
    };
    const handleInputOnChange = (e, scKey) => {
        const previousValue = getPreviousValue(scKey).toString();
        const value = e.target.value;
        const verifiedValue = verifyAndTransformValueIfNecessary(
            value,
            previousValue
        );
        validateInput(scKey, parseFloat(verifiedValue));
        if (scKey === SCENARIO_ONE_THRESHOLD)
            setS1ThresholdValue(verifiedValue);
        if (scKey === SCENARIO_TWO_PORTFOLIO_THRESHOLD)
            setS2PfThresholdValue(verifiedValue);
        if (scKey === SCENARIO_TWO_CONCENTRATION_THRESHOLD)
            setS2ConcThresholdValue(verifiedValue);
        if (scKey === SCENARIO_THREE_THRESHOLD)
            setS3ThresholdValue(verifiedValue);
        if (scKey === SCENARIO_FOUR_THRESHOLD)
            setS4ThresholdValue(verifiedValue);
    };

    const handleSubmitButtonOnClick = () => {
        const requestPayload = [
            {
                scenario: 1,
                thresholdType: SCENARIO_ONE_THRESHOLD,
                thresholdValue: s1ThresholdValue || null
            },
            {
                scenario: 2,
                thresholdType: SCENARIO_TWO_PORTFOLIO_THRESHOLD,
                thresholdValue: s2PfThresholdValue || null
            },
            {
                scenario: 2,
                thresholdType: SCENARIO_TWO_CONCENTRATION_THRESHOLD,
                thresholdValue: s2ConcThresholdValue || null
            },
            {
                scenario: 3,
                thresholdType: SCENARIO_THREE_THRESHOLD,
                thresholdValue: s3ThresholdValue || null
            },
            {
                scenario: 4,
                thresholdType: SCENARIO_FOUR_THRESHOLD,
                thresholdValue: s4ThresholdValue || null
            }
        ].reduce(
            (acc, curr) =>
                curr.thresholdValue
                    ? [
                          ...acc,
                          {
                              ...curr,
                              thresholdValue: parseFloat(curr.thresholdValue)
                          }
                      ]
                    : [
                          ...acc,
                          {
                              scenario: curr.scenario,
                              thresholdType: curr.thresholdType
                          }
                      ],
            []
        );
        addOverrides(requestPayload);
        dispatch(toggleOverallAppOverlay());
    };

    const validateInput = (id, value) => {
        if (value > 100) {
            !idsWithError.includes(id) &&
                setIdsWithError([...idsWithError, id]);
            setErrorMsg(BETWEEN_0_AND_100_ERROR_TEXT);
        } else {
            const removeIdFromErrorIdsArray = idsWithError.filter(
                (errorId) => errorId !== id
            );
            setIdsWithError(removeIdFromErrorIdsArray);
            if (removeIdFromErrorIdsArray.length === 0) setErrorMsg('');
        }
    };

    const setters = {
        setS1ThresholdValue: setS1ThresholdValue,
        setS2PfThresholdValue: setS2PfThresholdValue,
        setS2ConcThresholdValue: setS2ConcThresholdValue,
        setS3ThresholdValue: setS3ThresholdValue,
        setS4ThresholdValue: setS4ThresholdValue
    };

    return (
        showOverallAppOverlay && (
            <div className={styles.wrapper}>
                <div className={styles.titleContainer}>
                    <p>Settings</p>
                    <img
                        src={BlueCrossIcon}
                        alt={''}
                        className={styles.blueCrossIcon}
                        onClick={handleCrossIconOnClick}
                    />
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.thresholdsTextContainer}>
                        Thresholds
                    </div>
                    <div className={styles.valuesContainer}>
                        <div className={styles.valuesFirstSubContainer}>
                            <div className={styles.scText}>
                                System Calculated
                            </div>
                            <div className={styles.overrideText}>
                                User Override
                            </div>
                        </div>
                        {SCENARIOS.map((scenario, idx) => (
                            <div
                                key={`${scenario.title}`}
                                className={styles.valuesSubContainer}
                            >
                                <ScenarioValuesContainer
                                    data={scenario}
                                    settings={settings}
                                    inputValue={inputValueArray[idx]}
                                    onChange={handleInputOnChange}
                                    setters={setters}
                                    error={errorMsg}
                                    idsWithError={idsWithError}
                                />
                            </div>
                        ))}
                    </div>
                    <div className={styles.buttonContainer}>
                        <p className={styles.error}>{errorMsg}</p>
                        <button
                            disabled={!!errorMsg}
                            className={
                                doesAnyOfTheOverrideInputsHaveValue
                                    ? styles.activeButton
                                    : styles.button
                            }
                            onClick={
                                errorMsg ? null : handleSubmitButtonOnClick
                            }
                        >
                            Apply Thresholds
                        </button>
                    </div>
                </div>
            </div>
        )
    );
};

ScenarioValuesContainer.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string,
        scKeys: PropTypes.arrayOf(PropTypes.string)
    }),
    settings: PropTypes.shape({
        s1Threshold: PropTypes.number,
        s2ConcentrationThreshold: PropTypes.number,
        s2PortfolioThreshold: PropTypes.number,
        s3Threshold: PropTypes.number,
        s4Threshold: PropTypes.number
    }),
    inputValue: PropTypes.any,
    onChange: PropTypes.func,
    setters: PropTypes.shape({
        setS1ThresholdValue: PropTypes.func,
        setS2PfThresholdValue: PropTypes.func,
        setS2ConcThresholdValue: PropTypes.func,
        setS3ThresholdValue: PropTypes.func,
        setS4ThresholdValue: PropTypes.func
    }),
    error: PropTypes.string,
    idsWithError: PropTypes.arrayOf(PropTypes.string)
};

export default Settings;
